import { type ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';

import { IconAlertCircle, IconCheck, IconLock, IconSailboat, IconUpload } from '@tabler/icons-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

import '../../styles/BoatSettings.css';
import { PolarVisualEditor } from './PolarVisualEditor';
import { SHOW_BOAT_POLARS_EDITOR } from '../../config/featureVisibility';
import { showMessage } from '../../showMessage';

const API_BASE_URL_MISSING_KEY = 'profile.apiBaseMissing';
const NOTIF_ERROR = 'notifications.error';
const NOTIF_SUCCESS = 'notifications.success';
const BOAT_NAME_MAX_LENGTH = 255;

type BoatSettingsItem = {
  id: number;
  name: string;
  boat_type: string;
  boat_id?: number;
  owner_name?: string | null;
  owner_email?: string | null;
  is_owner?: boolean;
  sharing?: { isOwner?: boolean; owner?: { full_name?: string | null; email?: string } };
  polars_txt?: string | null;
  model_polars_txt?: string | null;
};

type BoatSettingsResponse = {
  success: boolean;
  message?: string;
  boats?: BoatSettingsItem[];
};

const isOwner = (boat: BoatSettingsItem): boolean => boat.is_owner ?? boat.sharing?.isOwner ?? true;

const resolveCustomPolars = (boat: BoatSettingsItem): string => boat.polars_txt?.trim() || '';

const resolveModelPolars = (boat: BoatSettingsItem): string => boat.model_polars_txt?.trim() || '';

const usesModelPolarsFallback = (boat: BoatSettingsItem): boolean =>
  resolveCustomPolars(boat).length === 0 && resolveModelPolars(boat).length > 0;

const resolvePolars = (boat: BoatSettingsItem): string =>
  resolveCustomPolars(boat) || resolveModelPolars(boat);

const polarExample = `TWA 6 8 10 12
45 5.2 5.8 6.3 6.8
60 6.1 6.8 7.2 7.6
90 6.8 7.5 8.0 8.4
120 6.4 7.2 7.9 8.5
150 5.8 6.6 7.4 8.1`;

const splitPolarRow = (row: string): string[] => (row.includes(';') ? row.split(';') : row.trim().split(/\s+/));

const countPolarColumns = (row: string): number => splitPolarRow(row).length;

const isFiniteNumericCell = (value: unknown): boolean => {
  const normalized = String(value ?? '').trim().replace(',', '.');
  return normalized.length > 0 && Number.isFinite(Number(normalized));
};

const isPositiveSpeedCell = (value: unknown): boolean => {
  const normalized = String(value ?? '').trim().replace(',', '.');
  if (normalized.length === 0) {
    return false;
  }
  const parsed = Number(normalized);
  return Number.isFinite(parsed) && parsed > 0;
};

const parsePolarNumber = (value: unknown): number | null => {
  const normalized = String(value ?? '').trim().replace(',', '.');
  if (normalized.length === 0) {
    return null;
  }
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const analyzeCertifiedPolarsJson = (value: string) => {
  try {
    const payload = JSON.parse(value) as unknown;
    const polars =
      payload && typeof payload === 'object' && 'polars' in payload
        ? (payload as { polars?: unknown }).polars
        : undefined;
    const boatSpeed =
      Array.isArray(polars) &&
      polars.find((item) => {
        if (!item || typeof item !== 'object') {
          return false;
        }
        const metric = String((item as { metric?: unknown }).metric ?? '')
          .toLowerCase()
          .split(/\s+/)
          .join('');
        return ['boatspeed', 'bsp', 'speed'].includes(metric);
      });
    const polarData =
      boatSpeed && typeof boatSpeed === 'object' ? (boatSpeed as { polarData?: unknown }).polarData : undefined;
    if (!Array.isArray(polarData)) {
      return null;
    }
    const points = polarData.filter((point) => {
      if (!point || typeof point !== 'object') {
        return false;
      }
      const values = point as { tws?: unknown; twa?: unknown; metricValue?: unknown };
      return (
        isFiniteNumericCell(values.tws) &&
        isFiniteNumericCell(values.twa) &&
        isPositiveSpeedCell(values.metricValue)
      );
    });
    if (points.length === 0) {
      return { rows: 0, columns: 0, valid: false, message: 'JSON polarData has no valid points' };
    }
    const twsCount = new Set(points.map((point) => Number((point as { tws?: unknown }).tws))).size;
    return { rows: points.length, columns: twsCount, valid: true, message: 'Certified JSON polar format' };
  } catch {
    return null;
  }
};

const analyzePolars = (value: string) => {
  const trimmed = value.trim();
  if (trimmed.startsWith('{')) {
    return (
      analyzeCertifiedPolarsJson(trimmed) ?? {
        rows: 0,
        columns: 0,
        valid: false,
        message: 'JSON must include polars[].polarData with BoatSpeed values',
      }
    );
  }

  const rows = value
    .split(/\r?\n/)
    .map((row) => row.trim())
    .filter(Boolean);

  if (rows.length === 0) {
    return { rows: 0, columns: 0, valid: true, message: '' };
  }

  const columns = countPolarColumns(rows[0]);
  if (rows.length < 2 || columns < 2) {
    return {
      rows: Math.max(rows.length - 1, 0),
      columns: Math.max(columns - 1, 0),
      valid: false,
      message: 'Table needs a TWA header and at least one TWS column',
    };
  }

  const header = splitPolarRow(rows[0]);
  if (header.slice(1).some((cell) => !isFiniteNumericCell(cell))) {
    return {
      rows: Math.max(rows.length - 1, 0),
      columns: Math.max(columns - 1, 0),
      valid: false,
      message: 'TWS header values must be numeric',
    };
  }

  let positiveSpeeds = 0;
  for (const row of rows.slice(1)) {
    const cells = splitPolarRow(row);
    if (cells.length !== columns) {
      return {
        rows: Math.max(rows.length - 1, 0),
        columns: Math.max(columns - 1, 0),
        valid: false,
        message: 'Polar rows should keep the same number of columns',
      };
    }
    if (!isFiniteNumericCell(cells[0])) {
      return {
        rows: Math.max(rows.length - 1, 0),
        columns: Math.max(columns - 1, 0),
        valid: false,
        message: 'TWA values must be numeric',
      };
    }
    for (const rawSpeed of cells.slice(1)) {
      const speedToken = rawSpeed.split('±')[0].trim();
      if (speedToken.length === 0) {
        continue;
      }
      const speed = parsePolarNumber(speedToken);
      if (speed === null || speed < 0) {
        return {
          rows: Math.max(rows.length - 1, 0),
          columns: Math.max(columns - 1, 0),
          valid: false,
          message: 'BoatSpeed values must be numeric',
        };
      }
      if (speed > 0) {
        positiveSpeeds += 1;
      }
    }
  }

  const valid = positiveSpeeds > 0;
  return {
    rows: Math.max(rows.length - 1, 0),
    columns: Math.max(columns - 1, 0),
    valid,
    message: valid ? '' : 'Table has no valid speed values',
  };
};

export const BoatSettings = () => {
  const { t } = useTranslation('common');
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const errorRef = useRef<HTMLDivElement | null>(null);
  const [boats, setBoats] = useState<BoatSettingsItem[]>([]);
  const [selectedBoatId, setSelectedBoatId] = useState<number | null>(null);
  const [nameDraft, setNameDraft] = useState('');
  const [polarsDraft, setPolarsDraft] = useState('');
  const [initialDraft, setInitialDraft] = useState({ name: '', polars: '' });
  const [shouldResetToModelPolars, setShouldResetToModelPolars] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  const selectedBoat = useMemo(
    () => boats.find((boat) => boat.id === selectedBoatId) ?? boats[0],
    [boats, selectedBoatId],
  );
  const selectedIsOwner = selectedBoat ? isOwner(selectedBoat) : false;
  const selectedUsesModelPolars = selectedBoat ? usesModelPolarsFallback(selectedBoat) : false;
  const polarStats = useMemo(() => analyzePolars(polarsDraft), [polarsDraft]);
  const hasChanges =
    nameDraft !== initialDraft.name ||
    (SHOW_BOAT_POLARS_EDITOR && (polarsDraft !== initialDraft.polars || shouldResetToModelPolars));

  useEffect(() => {
    if (error) {
      errorRef.current?.focus();
    }
  }, [error]);

  useEffect(() => {
    const loadBoats = async () => {
      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      if (!baseUrl) {
        showMessage('error', t(NOTIF_ERROR), t(API_BASE_URL_MISSING_KEY));
        setIsLoading(false);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const response = await axios.post<BoatSettingsResponse>(
          `${baseUrl}/api/user/getBoatSettings`,
          {},
          { headers: { Authorization: `Bearer ${token ?? ''}` } },
        );
        const payload = response.data;
        if (!payload.success) {
          throw new Error(payload.message ?? t('settings.boats.loadError', 'Could not load boats'));
        }
        const nextBoats = payload.boats ?? [];
        setBoats(nextBoats);
        const firstBoat = nextBoats[0];
        if (firstBoat) {
          setSelectedBoatId(firstBoat.id);
          const nextPolars = resolvePolars(firstBoat);
          setNameDraft(firstBoat.name);
          setPolarsDraft(nextPolars);
          setInitialDraft({ name: firstBoat.name, polars: nextPolars });
          setShouldResetToModelPolars(false);
        }
      } catch (unknownError) {
        const message =
          unknownError instanceof Error
            ? unknownError.message
            : t('settings.boats.loadError', 'Could not load boats');
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    void loadBoats();
  }, [t]);

  const selectBoat = (boat: BoatSettingsItem) => {
    setSelectedBoatId(boat.id);
    const nextPolars = resolvePolars(boat);
    setNameDraft(boat.name);
    setPolarsDraft(nextPolars);
    setInitialDraft({ name: boat.name, polars: nextPolars });
    setShouldResetToModelPolars(false);
    setError('');
  };

  const resetDraft = () => {
    setNameDraft(initialDraft.name);
    setPolarsDraft(initialDraft.polars);
    setShouldResetToModelPolars(false);
  };

  const updatePolarsDraft = (nextPolars: string) => {
    setPolarsDraft(nextPolars);
    setShouldResetToModelPolars(false);
  };

  const restoreModelPolars = () => {
    if (!selectedBoat) {
      return;
    }
    const modelPolars = resolveModelPolars(selectedBoat);
    if (modelPolars.length === 0) {
      return;
    }
    setPolarsDraft(modelPolars);
    setShouldResetToModelPolars(true);
    setError('');
  };

  const importPolarFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }
    try {
      const content = await file.text();
      updatePolarsDraft(content.trim());
      setError('');
    } catch {
      setError(t('settings.boats.fileReadError', 'Could not read polar file'));
    }
  };

  const saveBoat = async () => {
    if (!selectedBoat || !selectedIsOwner || !(SHOW_BOAT_POLARS_EDITOR ? polarStats.valid : true)) {
      return;
    }
    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    if (!baseUrl) {
      showMessage('error', t(NOTIF_ERROR), t(API_BASE_URL_MISSING_KEY));
      return;
    }
    const trimmedName = nameDraft.trim();
    if (trimmedName.length === 0) {
      setError(t('settings.boats.nameRequired', 'Boat name is required'));
      return;
    }
    if (trimmedName.length > BOAT_NAME_MAX_LENGTH) {
      setError(
        t('settings.boats.nameTooLong', 'Boat name must be {{count}} characters or fewer', {
          count: BOAT_NAME_MAX_LENGTH,
        }),
      );
      return;
    }
    const customPolars = resolveCustomPolars(selectedBoat);
    const modelPolars = resolveModelPolars(selectedBoat);
    const shouldPersistEmptyPolars =
      shouldResetToModelPolars ||
      (customPolars.length === 0 && modelPolars.length > 0 && polarsDraft.trim() === modelPolars);
    const persistedPolars = SHOW_BOAT_POLARS_EDITOR
      ? shouldPersistEmptyPolars
        ? ''
        : polarsDraft.trim()
      : customPolars;

    setIsSaving(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${baseUrl}/api/user/updateBoatSettings`,
        {
          boatId: selectedBoat.id,
          name: trimmedName,
          polarsTxt: persistedPolars,
        },
        { headers: { Authorization: `Bearer ${token ?? ''}` } },
      );
      setBoats((previous) =>
        previous.map((boat) =>
          boat.id === selectedBoat.id
            ? { ...boat, name: trimmedName, polars_txt: persistedPolars }
            : boat,
        ),
      );
      setInitialDraft({
        name: trimmedName,
        polars: SHOW_BOAT_POLARS_EDITOR ? polarsDraft.trim() : initialDraft.polars,
      });
      setNameDraft(trimmedName);
      setPolarsDraft(SHOW_BOAT_POLARS_EDITOR ? polarsDraft.trim() : initialDraft.polars);
      setShouldResetToModelPolars(false);
      showMessage('success', t(NOTIF_SUCCESS), t('settings.boats.saveSuccess', 'Boat settings saved'));
    } catch (unknownError) {
      const message =
        unknownError instanceof Error
          ? unknownError.message
          : t('settings.boats.saveError', 'Could not save boat settings');
      setError(message);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="settings-form-container-boats">
        <div className="boat-settings-skeleton" aria-label={t('settings.boats.loading', 'Loading boats')} />
      </div>
    );
  }

  if (boats.length === 0) {
    return (
      <div className="settings-form-container-boats">
        <div className="boat-empty-state">
          <IconSailboat size={34} stroke={1.6} aria-hidden="true" />
          <h2>{t('settings.boats.emptyTitle', 'No boats yet')}</h2>
          <p>
            {SHOW_BOAT_POLARS_EDITOR
              ? t('settings.boats.emptyBody', 'Create a boat before adding custom polars.')
              : t('settings.boats.emptySettingsBody', 'Create a boat before editing settings.')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <section className="settings-form-container-boats" aria-labelledby="boat-settings-title">
      <div className="boat-settings-header">
        <div>
          <h2 id="boat-settings-title">{t('settings.boats.title', 'Boat settings')}</h2>
          <p>
            {SHOW_BOAT_POLARS_EDITOR
              ? t('settings.boats.subtitle', 'Edit boat names and custom polars for boats you own.')
              : t('settings.boats.basicSubtitle', 'Edit boat names for boats you own.')}
          </p>
        </div>
      </div>

      <div className="boat-settings-layout">
        <div className="boat-settings-list" aria-label={t('settings.boats.listLabel', 'Boats')}>
          {boats.map((boat) => {
            const owner = isOwner(boat);
            const active = selectedBoat?.id === boat.id;
            return (
              <button
                key={boat.id}
                type="button"
                className={`boat-settings-list-item${active ? ' active' : ''}`}
                onClick={() => selectBoat(boat)}
                aria-pressed={active}
              >
                <span className="boat-list-icon">
                  {owner ? <IconSailboat size={18} stroke={1.7} /> : <IconLock size={18} stroke={1.7} />}
                </span>
                <span className="boat-list-copy">
                  <strong>{boat.name}</strong>
                  <span>{boat.boat_type}</span>
                </span>
                <span className={`boat-owner-badge${owner ? ' owned' : ''}`}>
                  {owner ? t('settings.boats.owner', 'Owner') : t('settings.boats.shared', 'Shared')}
                </span>
              </button>
            );
          })}
        </div>

        <div className="boat-editor-panel">
          {selectedBoat && (
            <>
              {!selectedIsOwner && (
                <div className="boat-readonly-notice" role="status">
                  <IconLock size={18} stroke={1.8} aria-hidden="true" />
                  <span>
                    {t('settings.boats.readOnly', 'Only the owner can edit this boat')}
                    {selectedBoat.owner_name ? ` (${selectedBoat.owner_name})` : ''}
                  </span>
                </div>
              )}

              {error && (
                <div className="boat-error" role="alert" tabIndex={-1} ref={errorRef}>
                  <IconAlertCircle size={18} stroke={1.8} aria-hidden="true" />
                  <span>{error}</span>
                </div>
              )}

              <div className="boat-form-grid">
                <label className="boat-field">
                  <span>{t('settings.boats.nameLabel', 'Boat name')}</span>
                  <input
                    value={nameDraft}
                    onChange={(event) => setNameDraft(event.target.value)}
                    maxLength={BOAT_NAME_MAX_LENGTH}
                    disabled={!selectedIsOwner || isSaving}
                  />
                </label>

                {SHOW_BOAT_POLARS_EDITOR && (
                  <div className="boat-polar-summary" aria-label={t('settings.boats.polarSummary', 'Polar summary')}>
                    <span>
                      <IconCheck size={16} stroke={2} aria-hidden="true" />
                      {t('settings.boats.rows', '{{count}} rows', { count: polarStats.rows })}
                    </span>
                    <span>{t('settings.boats.twsColumns', '{{count}} TWS columns', { count: polarStats.columns })}</span>
                  </div>
                )}
              </div>

              {SHOW_BOAT_POLARS_EDITOR && (
                <>
                  <div
                    className="boat-field boat-field-polars"
                    role="group"
                    aria-label={t('settings.boats.polarsLabel', 'Custom polars')}
                  >
                    <span>{t('settings.boats.polarsLabel', 'Custom polars')}</span>
                    {selectedUsesModelPolars && (
                      <div className="boat-polars-source-notice" role="status">
                        <IconAlertCircle size={17} stroke={1.8} aria-hidden="true" />
                        <span>
                          {t(
                            'settings.boats.usingModelPolars',
                            'Showing this boat model defaults. Edit or import polars to save them as custom polars.',
                          )}
                        </span>
                      </div>
                    )}
                    <PolarVisualEditor
                      value={polarsDraft}
                      onChange={updatePolarsDraft}
                      disabled={!selectedIsOwner || isSaving}
                    />
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".json,.txt,.csv,application/json,text/plain,text/csv"
                      className="boat-file-input"
                      onChange={importPolarFile}
                      disabled={!selectedIsOwner || isSaving}
                    />
                  </div>
                  <p className={`boat-polar-hint${polarStats.valid ? '' : ' invalid'}`}>
                    {polarStats.valid
                      ? t(
                          'settings.boats.polarsHint',
                          'Paste certified JSON or a TWA/TWS table with semicolons or spaces.',
                        )
                      : t('settings.boats.polarsInvalid', polarStats.message)}
                  </p>
                </>
              )}

              <div className="boat-actions">
                {SHOW_BOAT_POLARS_EDITOR && (
                  <>
                    <button
                      type="button"
                      className="boat-secondary-button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={!selectedIsOwner || isSaving}
                    >
                      <IconUpload size={17} stroke={1.8} aria-hidden="true" />
                      {t('settings.boats.importFile', 'Import file')}
                    </button>
                    <button
                      type="button"
                      className="boat-secondary-button"
                      onClick={() => updatePolarsDraft(polarExample)}
                      disabled={!selectedIsOwner || isSaving}
                    >
                      {t('settings.boats.loadExample', 'Load example')}
                    </button>
                    {selectedBoat && resolveModelPolars(selectedBoat).length > 0 && (
                      <button
                        type="button"
                        className="boat-secondary-button"
                        onClick={restoreModelPolars}
                        disabled={!selectedIsOwner || isSaving}
                      >
                        {t('settings.boats.restoreDefaults', 'Restore defaults')}
                      </button>
                    )}
                  </>
                )}
                <button
                  type="button"
                  className="boat-secondary-button"
                  onClick={resetDraft}
                  disabled={!hasChanges || isSaving}
                >
                  {t('settings.boats.discard', 'Discard')}
                </button>
                <button
                  type="button"
                  className="boat-primary-button"
                  onClick={saveBoat}
                  disabled={
                    !selectedIsOwner ||
                    !hasChanges ||
                    !(SHOW_BOAT_POLARS_EDITOR ? polarStats.valid : true) ||
                    isSaving
                  }
                >
                  {isSaving
                    ? t('settings.boats.saving', 'Saving')
                    : t('settings.boats.save', 'Save changes')}
                </button>
              </div>

            </>
          )}
        </div>
      </div>
    </section>
  );
};
